import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatCurrency } from "@/components/StatusBadge";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";

const LINE_TYPES = [
  { v: "original_scope", label: "Original Scope" },
  { v: "additional_work", label: "Additional Work" },
  { v: "change_order", label: "Change Order" },
  { v: "deduction", label: "Deduction" },
  { v: "manual_adjustment", label: "Manual Adjustment" },
];
const LINE_STATUSES = [
  { v: "draft", label: "Draft" },
  { v: "approved", label: "Approved" },
  { v: "cancelled", label: "Cancelled" },
  { v: "completed", label: "Completed" },
];

const statusPill = (s) => {
  const map = {
    draft: "bg-slate-100 text-slate-700",
    approved: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    cancelled: "bg-rose-50 text-rose-800 border border-rose-200",
    completed: "bg-sky-50 text-sky-800 border border-sky-200",
  };
  return <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 ${map[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
};

export default function ScopeLinesSection({ project, onChanged }) {
  const { user } = useAuth();
  const [lines, setLines] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [current, setCurrent] = useState(0);
  const empty = {
    description: "", line_type: "additional_work",
    amount: 0, currency: project.currency, approval_date: "",
    status: "draft", notes: "",
    payment_stages: [],   // optional stages attached to this scope line
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get(`/api/projects/${project.id}/scope-lines`);
    setLines(data.lines || []);
    setCurrent(data.current_project_value || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const totals = useMemo(() => {
    const orig = lines.filter((l) => l.line_type === "original_scope" && ["approved", "completed"].includes(l.status)).reduce((s, l) => s + Number(l.amount || 0), 0);
    const add = lines.filter((l) => ["additional_work", "change_order", "manual_adjustment"].includes(l.line_type) && l.status === "approved").reduce((s, l) => s + Number(l.amount || 0), 0);
    const ded = lines.filter((l) => l.line_type === "deduction" && l.status === "approved").reduce((s, l) => s + Math.abs(Number(l.amount || 0)), 0);
    return { orig, add, ded };
  }, [lines]);

  const canEdit = hasRole(user, "admin", "business_development");

  const save = async () => {
    if (!form.description.trim()) return toast.error("Description required");
    if (Number(form.amount) === 0 && form.line_type !== "deduction") return toast.error("Amount required");
    // Validate attached stages when present
    const stagesEnabled = ["additional_work", "change_order"].includes(form.line_type);
    const stages = (stagesEnabled ? (form.payment_stages || []) : []).filter((s) => (s.stage_name || "").trim() || Number(s.fixed_amount || 0) > 0 || Number(s.percentage || 0) > 0);
    if (stages.length && !editing) {
      for (const s of stages) {
        if (!s.stage_name?.trim()) return toast.error("Every payment stage needs a name");
        if (!Number(s.fixed_amount) && !Number(s.percentage)) return toast.error(`Stage “${s.stage_name}” needs an amount or a percentage`);
      }
    }
    try {
      const body = {
        description: form.description,
        line_type: form.line_type,
        amount: Number(form.amount),
        currency: form.currency,
        approval_date: form.approval_date || null,
        status: form.status,
        notes: form.notes || null,
      };
      if (editing) {
        await api.patch(`/api/projects/${project.id}/scope-lines/${editing.id}`, body);
        toast.success("Scope line updated");
      } else {
        if (stages.length) {
          body.payment_stages = stages.map((s, i) => ({
            stage_number: i + 1,
            stage_name: s.stage_name.trim(),
            percentage: Number(s.percentage || 0),
            fixed_amount: Number(s.fixed_amount) ? Number(s.fixed_amount) : null,
            trigger: s.trigger || null,
            expected_invoice_date: s.expected_invoice_date || null,
            status: "not_due",
          }));
        }
        await api.post(`/api/projects/${project.id}/scope-lines`, body);
        toast.success(stages.length ? `Scope line + ${stages.length} payment stage${stages.length === 1 ? "" : "s"} added` : "Scope line added");
      }
      setOpenAdd(false); setEditing(null); setForm(empty);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addStageRow = () => setForm((f) => ({
    ...f,
    payment_stages: [...(f.payment_stages || []), {
      _key: crypto.randomUUID?.() || `s-${Date.now()}-${Math.random()}`,
      stage_name: "", percentage: 0, fixed_amount: 0, trigger: "", expected_invoice_date: "",
    }],
  }));
  const updateStageRow = (i, k, v) => setForm((f) => {
    const rows = [...(f.payment_stages || [])];
    rows[i] = { ...rows[i], [k]: v };
    return { ...f, payment_stages: rows };
  });
  const removeStageRow = (i) => setForm((f) => ({ ...f, payment_stages: (f.payment_stages || []).filter((_, idx) => idx !== i) }));

  const stagesTotal = (form.payment_stages || []).reduce((s, x) => {
    if (Number(x.fixed_amount)) return s + Number(x.fixed_amount);
    return s + (Number(form.amount || 0) * Number(x.percentage || 0) / 100);
  }, 0);

  const remove = async (l) => {
    if (!window.confirm(`Delete line "${l.description}"?`)) return;
    try {
      await api.delete(`/api/projects/${project.id}/scope-lines/${l.id}`);
      toast.success("Line deleted");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...empty, ...l, approval_date: l.approval_date?.slice(0, 10) || "" });
    setOpenAdd(true);
  };
  const openAddNew = () => { setEditing(null); setForm(empty); setOpenAdd(true); };

  return (
    <Card className="border-slate-200 shadow-none p-4 mt-4" data-testid="scope-lines-section">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SumTile label="Original Amount" value={totals.orig} cur={project.currency} />
        <SumTile label="Approved Additions" value={totals.add} cur={project.currency} tone="emerald" />
        <SumTile label="Approved Deductions" value={-totals.ded} cur={project.currency} tone="rose" />
        <SumTile label="Current Project Value" value={current} cur={project.currency} tone="slate-strong" />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">Scope / Change-Order Lines ({lines.length})</div>
        {canEdit && (
          <Button size="sm" onClick={openAddNew} data-testid="add-scope-line-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add line
          </Button>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left">
              <th className="px-3 py-2 w-10">#</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Added</th>
              <th className="px-3 py-2">Approved</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">By</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-slate-100" data-testid={`scope-line-row-${l.id}`}>
                <td className="px-3 py-2 tabular-nums text-slate-500">{l.line_number}</td>
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2 text-slate-600">{LINE_TYPES.find((t) => t.v === l.line_type)?.label || l.line_type}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{l.date_added?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{l.approval_date?.slice(0, 10) || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {l.line_type === "deduction" ? <span className="text-rose-700">-{formatCurrency(Math.abs(l.amount), l.currency)}</span>
                    : formatCurrency(l.amount, l.currency)}
                </td>
                <td className="px-3 py-2">{statusPill(l.status)}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{l.added_by_snapshot || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <>
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => openEdit(l)} data-testid={`edit-line-${l.id}`}><Pencil className="w-3 h-3" /></button>
                      <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => remove(l)} data-testid={`delete-line-${l.id}`}><Trash2 className="w-3 h-3" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-500 py-6">No scope lines. Add the first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={openAdd} onOpenChange={(v) => { if (!v) { setOpenAdd(false); setEditing(null); setForm(empty); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Scope Line" : "Add Scope Line"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="scope-desc" />
            </div>
            <div className="space-y-1.5">
              <Label>Line Type</Label>
              <Select value={form.line_type} onValueChange={(v) => setForm({ ...form, line_type: v })}>
                <SelectTrigger data-testid="scope-line-type"><SelectValue /></SelectTrigger>
                <SelectContent>{LINE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input type="number" step="any" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="scope-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Approval Date</Label>
              <Input type="date" value={form.approval_date || ""} onChange={(e) => setForm({ ...form, approval_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="scope-status"><SelectValue /></SelectTrigger>
                <SelectContent>{LINE_STATUSES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {/* Attached payment stages — only for Additional Work / Change Order,
                and only when creating (edits keep existing stages intact). */}
            {!editing && ["additional_work", "change_order"].includes(form.line_type) && (
              <div className="col-span-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-slate-600" />
                    <span className="text-sm font-medium">Payment Stages for this line (optional)</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addStageRow} data-testid="add-line-stage-btn">
                    <Plus className="w-3 h-3 mr-1" />Add stage
                  </Button>
                </div>
                {(form.payment_stages || []).length === 0 ? (
                  <div className="text-xs text-slate-500 py-2">
                    Skip if this line will be invoiced under the existing project stages, or add one or more stages to schedule its own invoices.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1.6fr_.7fr_.7fr_1fr_1fr_28px] gap-2 text-[10px] uppercase text-slate-500 px-1">
                      <div>Stage name</div>
                      <div>%</div>
                      <div>Fixed amount</div>
                      <div>Trigger</div>
                      <div>Invoice on</div>
                      <div></div>
                    </div>
                    {(form.payment_stages || []).map((s, i) => (
                      <div key={s._key || `stage-${i}`} className="grid grid-cols-[1.6fr_.7fr_.7fr_1fr_1fr_28px] gap-2 items-center" data-testid={`line-stage-row-${i}`}>
                        <Input value={s.stage_name} onChange={(e) => updateStageRow(i, "stage_name", e.target.value)} placeholder="e.g. On approval" />
                        <Input type="number" step="any" value={s.percentage || ""} onChange={(e) => updateStageRow(i, "percentage", e.target.value)} placeholder="0" />
                        <Input type="number" step="any" value={s.fixed_amount || ""} onChange={(e) => updateStageRow(i, "fixed_amount", e.target.value)} placeholder="0" />
                        <Input value={s.trigger || ""} onChange={(e) => updateStageRow(i, "trigger", e.target.value)} placeholder="milestone" />
                        <Input type="date" value={s.expected_invoice_date || ""} onChange={(e) => updateStageRow(i, "expected_invoice_date", e.target.value)} />
                        <button type="button" className="p-1 rounded hover:bg-rose-50 text-rose-500" onClick={() => removeStageRow(i)} data-testid={`remove-line-stage-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <div className="text-[11px] text-slate-500 flex justify-between px-1 pt-1">
                      <span>Fill either % of the line amount OR a fixed amount. Blank both = ignored.</span>
                      <span>Stages total: <b className="tabular-nums text-slate-800">{formatCurrency(stagesTotal, form.currency)}</b> / line {formatCurrency(Number(form.amount || 0), form.currency)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenAdd(false); setEditing(null); setForm(empty); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="save-scope-line-btn">{editing ? "Save Changes" : "Add Line"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SumTile({ label, value, cur, tone }) {
  const tones = {
    "slate-strong": "bg-slate-900 text-white border-slate-900",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  };
  const t = tones[tone] || "bg-slate-50 text-slate-800 border-slate-200";
  return (
    <div className={`rounded-lg border px-3 py-2 ${t}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{formatCurrency(value, cur)}</div>
    </div>
  );
}
